import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import type { SessionFeedback as SessionFeedbackType } from "@/types/session";

interface SessionFeedbackProps {
  onSubmit: (score: number, comment: string) => Promise<void> | void;
  feedback?: SessionFeedbackType | null;
  isSubmitting?: boolean;
  error?: string | null;
  className?: string;
}

export function SessionFeedback({
  onSubmit,
  feedback,
  isSubmitting = false,
  error,
  className = "",
}: SessionFeedbackProps) {
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [touched, setTouched] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (feedback) {
      setScore(feedback.npsScore);
      setComment(feedback.comment ?? "");
      setTouched(false);
      setIsEditing(false);
    } else {
      setScore(null);
      setComment("");
      setTouched(false);
      setIsEditing(false);
    }
  }, [feedback?.id, feedback?.submittedAt, feedback?.npsScore]);

  const hasFeedback = Boolean(feedback);

  const handleSubmit = async () => {
    if (!onSubmit || score === null || isSubmitting) {
      setTouched(true);
      return;
    }
    const trimmed = comment.trim();
    try {
      await Promise.resolve(onSubmit(score, trimmed));
      setTouched(false);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  // Get color based on score for visual feedback
  const getScoreColor = (value: number) => {
    if (value <= 3) return "bg-red-500 text-red-50";
    if (value <= 6) return "bg-amber-500 text-amber-50";
    return "bg-emerald-500 text-emerald-50";
  };

  if (hasFeedback && feedback && !isEditing) {
    return (
      <div className={`rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${getScoreColor(feedback.npsScore)} text-sm font-semibold`}>
              {feedback.npsScore}
            </span>
            <div>
              <h3 className="text-sm font-semibold text-emerald-900">Thanks for your feedback!</h3>
              <p className="text-xs text-emerald-800">Captured {new Date(feedback.submittedAt).toLocaleString()}</p>
            </div>
          </div>
          <button
            type="button"
            className="text-xs font-semibold text-emerald-800 underline decoration-dotted"
            onClick={() => {
              setIsEditing(true);
              setScore(feedback.npsScore);
              setComment(feedback.comment ?? "");
              setTouched(false);
            }}
          >
            Edit
          </button>
        </div>
        {feedback.comment && (
          <p className="mt-3 text-sm text-emerald-900">"{feedback.comment}"</p>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-border/60 bg-background/80 p-4 shadow-sm ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">How useful was this session?</h3>
          <p className="text-xs text-muted-foreground">Score 0–10. 0 = Not useful, 10 = Extremely useful.</p>
        </div>
        <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
          Simple NPS
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {Array.from({ length: 11 }, (_, index) => index).map((value) => {
          const isActive = score === value;
          // Get appropriate color based on score range
          const activeColor = value <= 3 
            ? "bg-red-500 text-red-50" 
            : value <= 6 
              ? "bg-amber-500 text-amber-50" 
              : "bg-emerald-500 text-emerald-50";
          
          return (
            <button
              key={value}
              type="button"
              onClick={() => {
                setScore(value);
                setTouched(false);
              }}
              className={`h-8 w-8 rounded-full text-xs font-semibold transition ${
                isActive
                  ? `${activeColor} shadow-lg`
                  : "border border-border bg-card text-muted-foreground hover:border-primary hover:text-primary"
              }`}
              aria-label={`Rate ${value} out of 10`}
            >
              {value}
            </button>
          );
        })}
      </div>

      <label className="mt-4 block text-xs font-medium text-muted-foreground">
        Anything we could do better?
        <textarea
          value={comment}
          onChange={(event) => setComment(event.target.value.slice(0, 280))}
          onBlur={() => setTouched(true)}
          placeholder="Share one or two lines (optional)"
          className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          rows={2}
          maxLength={280}
        />
        <span className="mt-1 block text-right text-[10px] text-muted-foreground">{comment.length}/280</span>
      </label>

      {touched && score === null && (
        <p className="mt-2 text-xs text-rose-500">Choose a score before submitting.</p>
      )}
      {error && (
        <p className="mt-2 text-xs text-rose-500">{error}</p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-sm font-semibold text-emerald-50 transition hover:bg-emerald-600 disabled:opacity-60"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Sending…
            </>
          ) : (
            'Send Feedback'
          )}
        </button>
        
        {hasFeedback && (
          <button
            type="button"
            onClick={() => {
              setIsEditing(false);
              setScore(feedback?.npsScore ?? null);
              setComment(feedback?.comment ?? "");
              setTouched(false);
            }}
            className="text-xs font-semibold text-muted-foreground underline decoration-dotted"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
